import { mergeAttributes, Node, textInputRule } from "@tiptap/core"
import Suggestion from "@tiptap/suggestion"
import { PasteRule, VueNodeViewRenderer, VueRenderer } from "@tiptap/vue-3"
import { debounce } from "debounce"
import { PluginKey } from "prosemirror-state"
import tippy from "tippy.js"
import { v4 as uuidv4 } from "uuid"
import { nextTick } from "vue"

import {
  dropFilePlugin,
  Epic,
  FileMetaData,
  Item,
  Milestone,
  Project,
  renderMilestoneName,
  SearchIdeaResult,
  Sprint,
  Task,
  Team,
  TypeConfig,
  User
} from "@/models"
import { router } from "@/router"
import { parsingProjectIdAndItemNumber } from "@/utils/helpers"

import { ReferenceType } from "./ReferenceType"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    reference: {
      setReference: (id: string, attrs: object) => ReturnType
      getReferenceType: () => string
    }
  }
}

export const ReferenceConfig = {
  [ReferenceType.USER]: {
    char: "@",
    label: "fullName",
    type: "referenceUser",
    extensionName: "referenceAt",
    renderTitle(user: User): string {
      return `${this.char}${user[this.label]}`
    }
  },
  [ReferenceType.PREVIOUS_TASK_OWNER]: {
    char: "@@",
    label: "fullName",
    type: "referenceUser",
    extensionName: "referenceAllUser",
    renderTitle(user: User): string {
      return `@${user[this.label]}`
    }
  },
  [ReferenceType.PROJECT]: {
    char: "#P",
    label: "name",
    type: "referenceProject",
    extensionName: "referenceHashProject",
    renderTitle: function (project: Project): string {
      return `${this.char} - ${project[this.label]}`
    }
  },
  [ReferenceType.MILESTONE]: {
    char: "#M",
    label: "title",
    type: "referenceMilestone",
    extensionName: "referenceHashMilestone",
    renderTitle: function (milestone: Milestone & { titleParsed?: string }): string {
      return `${this.char} - ${milestone.titleParsed ?? renderMilestoneName(milestone[this.label])}`
    }
  },
  [ReferenceType.EPIC]: {
    char: "#E",
    label: "name",
    type: "referenceEpic",
    extensionName: "referenceHashEpic",
    renderTitle: function (epic: Epic): string {
      return `${this.char} - ${epic.sequenceNumber} ${epic[this.label]}`
    }
  },
  [ReferenceType.IDEA]: {
    char: "#I",
    label: "title",
    type: "referenceIdea",
    extensionName: "referenceHashIdea",
    renderTitle: function (idea: SearchIdeaResult): string {
      return `${this.char} - ${idea.sequenceNumber} ${idea[this.label]}`
    }
  },
  [ReferenceType.TEAM]: {
    char: "#T",
    label: "name",
    type: "referenceTeam",
    extensionName: "referenceHashTeam",
    renderTitle: function (team: Team): string {
      return `${this.char} - ${team[this.label]}`
    }
  },
  [ReferenceType.SPRINT]: {
    char: "#S",
    label: "sequenceNumber",
    type: "referenceSprint",
    extensionName: "referenceHashSprint",
    renderTitle: function (sprint: Sprint): string {
      return `Sprint #${sprint[this.label]}`
    }
  },
  [ReferenceType.ITEM]: {
    char: "#",
    label: "sequenceNumber",
    type: "referenceItem",
    extensionName: "referenceHashItem",
    renderTitle: function (item: Item): string {
      return `#${item[this.label]} - ${item.title}`
    }
  },
  [ReferenceType.TASK]: {
    char: "#",
    label: "sequenceNumber",
    type: "referenceTask",
    extensionName: "referenceHashTask",
    renderTitle: function (task: Task, item: Item): string {
      const { sequenceNumber } = item

      return `#${sequenceNumber}.${task.order} - ${task.name}`
    }
  },
  [ReferenceType.FILE]: {
    char: "#",
    label: "",
    type: "",
    extensionName: "referenceHashFile",
    renderTitle: function (): string {
      return ""
    }
  },
  [ReferenceType.FILE_LINK]: {
    char: "#F",
    label: "name",
    type: "referenceFile",
    extensionName: "referenceHashFileLink",
    renderTitle: function (file: FileMetaData): string {
      return file[this.label]
    }
  },
  [ReferenceType.TIP]: {
    char: "#",
    label: "",
    type: "",
    extensionName: "referenceHashTip",
    renderTitle: function (): string {
      return ``
    }
  }
}

export const getTypeByText = search => {
  if (/@@/.test(search)) {
    return ReferenceType.PREVIOUS_TASK_OWNER
  }
  if (/@/.test(search)) {
    return ReferenceType.USER
  }

  if (/#[0-9]+($| )/.test(search)) {
    return ReferenceType.ITEM
  } else if (/#[0-9]+\./.test(search) || /#[0-9]+\.[0-9]+/.test(search)) {
    return ReferenceType.TASK
  } else if (/#P/i.test(search)) {
    return ReferenceType.PROJECT
  } else if (/#M/i.test(search)) {
    return ReferenceType.MILESTONE
  } else if (/#E/i.test(search)) {
    return ReferenceType.EPIC
  } else if (/#I/i.test(search)) {
    return ReferenceType.IDEA
  } else if (/#T/i.test(search)) {
    return ReferenceType.TEAM
  } else if (/#S/i.test(search)) {
    return ReferenceType.SPRINT
  } else if (/#F/i.test(search)) {
    return ReferenceType.FILE_LINK
  } else {
    return ReferenceType.TIP
  }
}

export const getTypeByUrl = (url: URL) => {
  const route = router.resolve(url.pathname + url.search)

  let {
    projectId: projectName,
    teamId: teamName,
    itemId: itemNumber,
    epicId: epicNumber,
    milestoneId: milestone,
    sprintId: sequenceNumber
  } = route.params

  let { task: taskNumber } = route.query

  if (route.name === "Item") {
    const result = parsingProjectIdAndItemNumber(projectName + "-" + itemNumber)
    projectName = result.projectId
    itemNumber = result.itemId
  }
  switch (route.name) {
    case "project":
      return {
        type: ReferenceType.PROJECT,
        name: projectName
      }
    case "create-team":
      return {
        type: ReferenceType.TEAM,
        name: teamName
      }
    case "Milestones":
      if (milestone) {
        return {
          type: ReferenceType.MILESTONE,
          number: milestone
        }
      }
      return undefined
    case "epic":
      if (route.query.i) {
        const queryItem = Array.isArray(route.query.i) ? route.query.i[0] : route.query.i
        const normalizedItemNumber =
          queryItem?.toString().match(/\d+$/)?.[0] || queryItem?.toString() || ""

        return {
          type: ReferenceType.ITEM,
          number: normalizedItemNumber
        }
      }
      return {
        type: ReferenceType.EPIC,
        number: epicNumber
      }
    case "ideas":
      if (route.query.idea) {
        return {
          type: ReferenceType.IDEA,
          number: route.query.idea
        }
      }
      return undefined
    case "sprint":
    case "SprintReview":
      return {
        type: ReferenceType.SPRINT,
        number: sequenceNumber || "active"
      }
    case "backlog":
      if (route.query.i) {
        const queryItem = Array.isArray(route.query.i) ? route.query.i[0] : route.query.i
        const queryString = queryItem?.toString() || ""

        if (queryString.includes("-")) {
          const result = parsingProjectIdAndItemNumber(queryString)
          return {
            type: ReferenceType.ITEM,
            number: result.itemId
          }
        }
        return {
          type: ReferenceType.ITEM,
          number: queryString.match(/\d+$/)?.[0] || queryString
        }
      }
      return {
        type: ReferenceType.SPRINT,
        number: "active"
      }
    case "Item": {
      return taskNumber
        ? {
            type: ReferenceType.TASK,
            number: taskNumber
          }
        : {
            type: ReferenceType.ITEM,
            number: itemNumber
          }
    }
  }

  if (route.query.i) {
    const queryItem = Array.isArray(route.query.i) ? route.query.i[0] : route.query.i
    const normalizedItemNumber =
      queryItem?.toString().match(/\d+$/)?.[0] || queryItem?.toString() || ""

    return {
      type: ReferenceType.ITEM,
      number: normalizedItemNumber
    }
  }
}

type ReferenceEditor = {
  referenceFileTargetConfig?: TypeConfig | null
  options: {
    extensions: Array<{ name: string }>
  }
}

const getReferenceFileTargetConfig = (editor: ReferenceEditor | null | undefined) =>
  editor?.referenceFileTargetConfig ?? null

const isExtensionExist = (editor: ReferenceEditor | null | undefined, type: ReferenceType) => {
  return editor?.options?.extensions.find(
    extension => extension.name === ReferenceConfig[type].extensionName
  )
}

const getTypeByTextForEditor = (editor: ReferenceEditor | null | undefined, search: string) => {
  const type = getTypeByText(search)

  if (editor && isExtensionExist(editor, type)) {
    return type
  }

  if (type === ReferenceType.FILE_LINK && editor && isExtensionExist(editor, ReferenceType.FILE)) {
    return ReferenceType.FILE
  }

  return type
}

export const ReferenceExtension = Node.create({
  name: "reference",
  inline: true,
  group: "inline",
  atom: true,
  draggable: true,

  addOptions() {
    return {
      suggestion: {
        pluginKey: new PluginKey(this.name),
        allowSpaces: false,
        render: () => {
          let component
          let popup
          let type
          const currentExtensionName = this.name

          return {
            onStart: props => {
              const { items, editor, clientRect } = props
              if (editor && (editor.isActive("codeBlock") || editor.isActive("link"))) return

              type = getTypeByTextForEditor(editor, props.text) ?? ReferenceType.TIP

              if (
                !isExtensionExist(editor, type) ||
                ReferenceConfig[type].extensionName !== currentExtensionName
              )
                return

              if (items?.component) {
                component = new VueRenderer(items.component, {
                  props: Object.assign(props, {
                    componentType: type,
                    referenceFileTargetConfig: getReferenceFileTargetConfig(editor)
                  }),
                  editor
                })

                popup = tippy("body", {
                  getReferenceClientRect: clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: "manual",
                  maxWidth: "auto",
                  placement: "bottom",
                  popperOptions: {
                    strategy: "fixed",
                    modifiers: [
                      {
                        name: "flip",
                        options: {
                          fallbackPlacements: [
                            "top",
                            "top-start",
                            "top-end",
                            "bottom",
                            "bottom-start",
                            "bottom-end"
                          ]
                        }
                      },
                      {
                        name: "preventOverflow",
                        options: {
                          altAxis: true,
                          rootBoundary: "viewport"
                        }
                      }
                    ]
                  }
                })

                if (popup && popup[0] && !popup[0].state.isShown) {
                  popup[0].show()
                }
              }
            },

            onUpdate: debounce(props => {
              if (
                (props?.editor &&
                  (props.editor.isActive("codeBlock") || props.editor.isActive("link"))) ||
                !props.clientRect ||
                !props.clientRect()
              )
                return
              const updatedType = getTypeByTextForEditor(props.editor, props.text)
              if (
                props?.editor &&
                (!isExtensionExist(props.editor, updatedType) ||
                  ReferenceConfig[updatedType].extensionName !== currentExtensionName)
              ) {
                if (popup && popup[0]) {
                  popup[0].destroy()
                }
                if (component) {
                  component.destroy()
                }
                return
              }

              if (type !== updatedType) {
                type = updatedType
                if (popup && popup[0]) {
                  popup[0].destroy()
                }
                if (props?.items?.component) {
                  component = new VueRenderer(props.items.component, {
                    props: Object.assign(props, {
                      componentType: type,
                      referenceFileTargetConfig: getReferenceFileTargetConfig(props.editor)
                    }),
                    editor: props.editor
                  })

                  popup = tippy("body", {
                    getReferenceClientRect: props.clientRect,
                    appendTo: () => document.body,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: "manual",
                    maxWidth: "auto",
                    placement: "bottom",
                    popperOptions: {
                      strategy: "fixed",
                      modifiers: [
                        {
                          name: "flip",
                          options: {
                            fallbackPlacements: [
                              "top",
                              "top-start",
                              "top-end",
                              "bottom",
                              "bottom-start",
                              "bottom-end"
                            ]
                          }
                        },
                        {
                          name: "preventOverflow",
                          options: {
                            altAxis: true,
                            rootBoundary: "viewport"
                          }
                        }
                      ]
                    }
                  })
                }
              }

              if (popup && popup[0]) {
                if (!popup[0].state.isShown) {
                  popup[0].show()
                }
                popup[0].setProps({
                  getReferenceClientRect: props.clientRect
                })

                nextTick(() => {
                  if (component?.ref?.queryUpdated) {
                    component.ref.queryUpdated(props.query)
                  }
                })
              }
            }, 500),

            onKeyDown(props) {
              if (
                props?.editor &&
                (props.editor.isActive("codeBlock") || props.editor.isActive("link"))
              )
                return
              const updatedType = getTypeByTextForEditor(props.editor, props.text)
              if (
                props?.editor &&
                (!isExtensionExist(props.editor, updatedType) ||
                  ReferenceConfig[updatedType].extensionName !== currentExtensionName)
              )
                return

              if (props.event.key === "Escape") {
                popup?.[0]?.hide()
                return true
              }

              if (component?.ref && typeof component?.ref?.onKeyDown === "function") {
                return component?.ref?.onKeyDown(props) ?? false
              }
            },

            onExit() {
              if (popup && popup[0]) {
                popup[0].destroy()
              }
              if (component) {
                component.destroy()
              }
            }
          }
        },
        command: ({ editor, props }) => {
          if (!props?.configType || !editor.view.state) return

          const extensionName = ReferenceConfig[props.configType]?.extensionName

          if (!extensionName) return
          if (extensionName !== this.name) return

          const stateKeys = Object.keys(editor.view.state)
          const fieldName = stateKeys.find(key => key.includes(extensionName))

          if (!fieldName) return

          const extension = editor.view.state[fieldName]

          if (!extension) return

          const deleteFrom = extension.range?.from ?? 0
          const deleteTo = extension.range?.to ?? 0

          if (!deleteTo) return
          editor
            .chain()
            .deleteRange({ from: deleteFrom, to: deleteTo })
            .insertContent([
              { type: this.name, attrs: { ...props, id: props.id || uuidv4() } },
              { type: "text", text: " " }
            ])
            .run()
          editor.view.focus()
        }
      }
    }
  },

  addProseMirrorPlugins() {
    const plugins =
      this.options.withSuggestion === false
        ? []
        : [
            Suggestion({
              editor: this.editor,
              ...this.options.suggestion
            })
          ]

    if (this.name === "referenceHashFile") {
      plugins.push(
        dropFilePlugin({
          editor: this.editor
        })
      )
    }

    return plugins
  },

  addCommands() {
    return {
      setReference:
        (title: string) =>
        ({ commands }) =>
          commands.setNode(this.name, { title }),
      getReferenceType: () => this.name
    }
  },

  addAttributes() {
    return {
      id: { default: "" },
      loading: { default: false },
      showDetails: { default: false },
      type: { default: "" },
      configType: { default: "" },
      info: { default: "" },
      linkText: { default: null },
      linkHref: { default: "" },
      originUrl: { default: "" },
      title: { default: null },
      ext: { default: "img" },
      width: {
        default: 300
      },
      height: {
        default: 300
      }
    }
  },

  parseHTML() {
    return [
      {
        tag: "reference",
        getAttrs: element => {
          const configType = (element as HTMLElement).getAttribute("configType") as ReferenceType

          return ReferenceConfig[configType]?.extensionName === this.name ? null : false
        }
      }
    ]
  },

  renderHTML({ HTMLAttributes }) {
    HTMLAttributes.type = ReferenceConfig[HTMLAttributes.configType].type
    const data = window.btoa(encodeURI(JSON.stringify(HTMLAttributes)))

    return ["reference", mergeAttributes(HTMLAttributes, { data })]
  },

  addNodeView() {
    return VueNodeViewRenderer(this.options.component)
  },

  renderText({ node }) {
    const { linkText } = node.attrs
    return linkText
  },

  addInputRules() {
    return [textInputRule({ find: /-___-$/, replace: "😑 " })]
  },

  addPasteRules() {
    if (!this.options.pasteRules) return []

    return [
      new PasteRule({
        find: new RegExp(this.options.pasteRules, "g"),
        handler: ({ state, range, match }) => {
          const { tr } = state

          if (match[0]) {
            if (this.options.suggestion.pluginKey?.key?.includes("referenceLink")) {
              const paramsFromUrl = window.location.pathname.split("/")
              const url = new URL(match[0])
              const params = url.pathname.split("/")
              const config = getTypeByUrl(url)

              if (
                config?.type &&
                (params[1] === paramsFromUrl[1] || config.type === ReferenceType.SPRINT)
              ) {
                const type = ReferenceConfig[config.type].type
                let linkText = url.pathname + (config.type === ReferenceType.TASK ? url.search : "")
                if (config.type === ReferenceType.ITEM && url.pathname.includes("backlog")) {
                  linkText = `${url.pathname.split("/").slice(0, -1).join("/")}/items/${config.number}`
                }
                if (config.type === ReferenceType.SPRINT && config.number === "active") {
                  linkText = url.pathname
                }
                const node = this.type.create({
                  loading: true,
                  configType: config.type,
                  type,
                  linkText,
                  originUrl: url.origin + url.pathname + url.search
                })

                tr.replaceWith(range.from, range.to, node)
              }
            } else {
              const configType = getTypeByTextForEditor(this.editor, match[0])
              if (ReferenceConfig[configType].extensionName !== this.name) return

              const type = ReferenceConfig[configType].type
              const node = this.type.create({
                loading: true,
                configType,
                type,
                linkText: match[0].trim()
              })

              tr.replaceWith(range.from, range.to, node)
            }
          }
        }
      })
    ]
  }
})
